import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Analytics,
  TrendingUp,
  Assessment,
  Timeline,
  CompareArrows,
  AutoAwesome,
  Refresh,
  Download,
  Settings,
  Visibility,
  Schedule,
  BarChart,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ProjectPerformanceChart,
  CostAnalysisChart,
  GanttComparisonView,
  PrimaveraIntegration,
  PowerBIStyleDashboard,
  ScheduleComparisonChart,
  AIInsightsPanel,
} from '../components/Analytics';
import { analyticsService, AnalyticsFilters } from '../services/analyticsService';
import { projectsService } from '../services/projectsService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `analytics-tab-${index}`,
    'aria-controls': `analytics-tabpanel-${index}`,
  };
}

const AnalyticsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('30d');
  const [aiInsightsEnabled, setAiInsightsEnabled] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [analyticsData, setAnalyticsData] = useState<any>(null);

  useEffect(() => {
    fetchProjects();
    fetchAnalyticsData();
  }, [selectedProject, dateRange]);

  const fetchProjects = async () => {
    try {
      const response = await projectsService.getAll();
      setProjects(response.data.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    }
  };

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      const filters: AnalyticsFilters = {
        project: selectedProject === 'all' ? undefined : selectedProject,
        dateRange: dateRange,
        includeAI: aiInsightsEnabled,
      };
      
      const data = await analyticsService.getDashboardData(filters);
      setAnalyticsData(data);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRefresh = () => {
    fetchAnalyticsData();
  };

  const handleExportData = async () => {
    try {
      const filters: AnalyticsFilters = {
        project: selectedProject === 'all' ? undefined : selectedProject,
        dateRange: dateRange,
        includeAI: aiInsightsEnabled,
      };
      
      const blob = await analyticsService.exportAnalyticsData(filters, 'pdf');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-report-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting analytics data:', error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Analytics color="primary" />
            AI Analytics Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Advanced project analytics with AI insights and schedule comparisons
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={handleRefresh} disabled={loading}>
            <Refresh />
          </IconButton>
          <IconButton onClick={handleExportData}>
            <Download />
          </IconButton>
          <IconButton onClick={() => setSettingsOpen(true)}>
            <Settings />
          </IconButton>
        </Box>
      </Box>

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 200, flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
              <FormControl fullWidth size="small">
                <InputLabel>Project</InputLabel>
                <Select
                  value={selectedProject}
                  label="Project"
                  onChange={(e) => setSelectedProject(e.target.value)}
                >
                  <MenuItem value="all">All Projects</MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project._id} value={project._id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ minWidth: 200, flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
              <FormControl fullWidth size="small">
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRange}
                  label="Date Range"
                  onChange={(e) => setDateRange(e.target.value)}
                >
                  <MenuItem value="7d">Last 7 Days</MenuItem>
                  <MenuItem value="30d">Last 30 Days</MenuItem>
                  <MenuItem value="90d">Last 90 Days</MenuItem>
                  <MenuItem value="1y">Last Year</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={aiInsightsEnabled}
                    onChange={(e) => setAiInsightsEnabled(e.target.checked)}
                  />
                }
                label="AI Insights"
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  icon={<AutoAwesome />}
                  label="AI Powered"
                  color="primary"
                  variant="outlined"
                  size="small"
                />
                <Chip
                  icon={<Timeline />}
                  label="Real-time"
                  color="success"
                  variant="outlined"
                  size="small"
                />
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="analytics tabs">
          <Tab
            label="Power BI Dashboard"
            icon={<BarChart />}
            iconPosition="start"
            {...a11yProps(0)}
          />
          <Tab
            label="Schedule Analysis"
            icon={<Schedule />}
            iconPosition="start"
            {...a11yProps(1)}
          />
          <Tab
            label="Gantt Comparison"
            icon={<CompareArrows />}
            iconPosition="start"
            {...a11yProps(2)}
          />
          <Tab
            label="AI Insights"
            icon={<AutoAwesome />}
            iconPosition="start"
            {...a11yProps(3)}
          />
          <Tab
            label="Primavera Integration"
            icon={<Timeline />}
            iconPosition="start"
            {...a11yProps(4)}
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <PowerBIStyleDashboard
          data={analyticsData}
          loading={loading}
          selectedProject={selectedProject}
          dateRange={dateRange}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <ScheduleComparisonChart
            data={analyticsData?.scheduleData}
            loading={loading}
          />
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ flex: 1 }}>
              <ProjectPerformanceChart
                data={analyticsData?.performanceData}
                loading={loading}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <CostAnalysisChart
                data={analyticsData?.costData}
                loading={loading}
              />
            </Box>
          </Box>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <GanttComparisonView
          data={analyticsData?.ganttData}
          loading={loading}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <AIInsightsPanel
          projectData={analyticsData?.aiInsights}
          loading={loading}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <PrimaveraIntegration />
      </TabPanel>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Analytics Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={aiInsightsEnabled}
                  onChange={(e) => setAiInsightsEnabled(e.target.checked)}
                />
              }
              label="Enable AI Insights"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Refresh Interval (minutes)"
              type="number"
              defaultValue={5}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={<Switch defaultChecked />}
              label="Real-time Updates"
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={<Switch defaultChecked />}
              label="Email Notifications"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
          <Button onClick={() => setSettingsOpen(false)} variant="contained">
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AnalyticsDashboard;